import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { TUI_DATE_FORMAT, TUI_DATE_SEPARATOR, TuiDay } from "@taiga-ui/cdk";
import { checkObjectKeyExists, isNotEmptyValue, isNull } from "kwikid-toolkit";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { throwErrorMessage } from "../../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./input-date.constants";
import { TKwikUIInputDateSize } from "./input-date.definitions";
import { isValidKeyValue } from "./input-date.validation";

@Component({
  selector: "kwikui-input-date",
  templateUrl: "./input-date.component.html",
  styleUrls: ["./input-date.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUIInputDateComponent),
      multi: true
    },
    { provide: TUI_DATE_FORMAT, useValue: `DMY` },
    { provide: TUI_DATE_SEPARATOR, useValue: `-` }
  ]
})
export class KwikUIInputDateComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() disabled: boolean = DEFAULT_VALUES.disabled;

  @Input() focus: boolean = DEFAULT_VALUES.focus;

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName: string = DEFAULT_VALUES.formControlName;

  @Input() hintContent: string = DEFAULT_VALUES.hintContent;

  @Input() icon: string = DEFAULT_VALUES.icon;

  @Input() id: string = DEFAULT_VALUES.id;

  @Input() invalid: boolean = DEFAULT_VALUES.invalid;

  @Input() label: string = DEFAULT_VALUES.label;

  @Input() max: any = DEFAULT_VALUES.max;

  @Input() maxCalculatedFromToday: {
    year?: number;
    month?: number;
    day?: number;
  } = DEFAULT_VALUES.maxCalculatedFromToday;

  @Input() min: any = DEFAULT_VALUES.min;

  @Input() minCalculatedFromToday: {
    year?: number;
    month?: number;
    day?: number;
  } = { year: 0, month: 0, day: 0 };

  @Input() placeholder: string = DEFAULT_VALUES.placeholder;

  @Input() postfix: string = DEFAULT_VALUES.postfix;

  @Input() prefix: string = DEFAULT_VALUES.prefix;

  @Input() properties = { readOnly: false };

  @Input() size: TKwikUIInputDateSize = DEFAULT_VALUES.size;

  @Input() validators = { required: true };

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  formGroup: FormGroup = new FormGroup({});

  subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    this.formGroup.addControl(this.formControlName, this.formControl);
    this.focus = this.focus === undefined ? false : this.focus;
    this.invalid =
      this.invalid === undefined ? false : this.invalid && this.focus;
    this.validators = { ...this.validators };
    this.properties = { ...this.properties };
    this.setDisabled();

    this.min = isNotEmptyValue(this.min)
      ? new TuiDay(this.min.year, this.min.month, this.min.day)
      : this.calculateMinDateFromToday(this.minCalculatedFromToday);
    this.max = isNotEmptyValue(this.max)
      ? new TuiDay(this.max.year, this.max.month, this.max.day)
      : this.calculateMaxDateFromToday(this.maxCalculatedFromToday);

    this.subscriptions.add(
      this.formGroup.controls[this.formControlName].valueChanges
        .pipe(distinctUntilChanged()) // makes sure the value has actually changed.
        .subscribe((value) => this.handleInputValueChange(value))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const verifyChange = (key: string) => {
      return changes.hasOwnProperty(key) && !changes[key].firstChange;
    };

    for (const change of Object.entries(changes)) {
      const key: string = change[0];
      const value: any = change[1].currentValue;
      this.validateInputProperty(key, value);
    }

    if (verifyChange("minCalculatedFromToday")) {
      this.min = isNotEmptyValue(this.min)
        ? new TuiDay(this.min.year, this.min.month, this.min.day)
        : this.calculateMinDateFromToday(this.minCalculatedFromToday);
    }
    if (verifyChange("maxCalculatedFromToday")) {
      this.max = isNotEmptyValue(this.max)
        ? new TuiDay(this.max.year, this.max.month, this.max.day)
        : this.calculateMaxDateFromToday(this.maxCalculatedFromToday);
    }

    if (verifyChange("formControl")) {
      this.formControl = changes.formControl.currentValue;
    }
    if (verifyChange("invalid")) {
      this.invalid = Boolean(changes.invalid.currentValue);
      this.setError();
    }
    if (verifyChange("focus")) {
      this.focus = Boolean(changes.focus.currentValue);
    }
    if (verifyChange("disabled")) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.setDisabled();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // ensure when component is destroyed the subscription is also and not left open.
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-input-date",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setReset() {
    this.formGroup.controls[this.formControlName].reset();
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setError() {
    if (
      this.formGroup.controls[this.formControlName] !== undefined &&
      this.formGroup.controls[this.formControlName].invalid
    ) {
      this.invalid = true;
      this.focus = true;
    } else {
      this.invalid = false;
      this.focus = false;
    }
  }

  /**
   * @description Handles firing of 2 events on (keyup) event
   *
   * @param value
   */
  handleInputValueChange(value: any) {
    if (isNull(value)) {
      this.emitEvent(this.getKeyValue, {
        key: this.formControlName,
        value: null
      });
    } else {
      const dateValue = new Date(value.year, value.month, value.day);

      const minDateValue = new Date(
        this.min.year,
        this.min.month,
        this.min.day
      );
      const maxDateValue = new Date(
        this.max.year,
        this.max.month,
        this.max.day
      );

      if (minDateValue <= dateValue && maxDateValue >= dateValue) {
        this.invalid = false;
        this.focus = false;
        if (value === this.formControl.value) {
          this.emitEvent(this.getKeyValue, {
            key: this.formControlName,
            value: this.formControl.value
          });
        }
      } else {
        this.setReset();
        this.setError();
      }
    }
  }

  handleFocusedChange(e: any) {
    if (!this.invalid) {
      this.focus = e;
    }
  }

  /**
   * Calculates the TuiDay object for the min date based on the minCalculatedFromToday input
   * @returns TuiDay
   */
  calculateMinDateFromToday(minCalculatedFromToday: {
    year?: number;
    month?: number;
    day?: number;
  }): TuiDay {
    const currentDate = new Date();

    let minYear = 0;
    let minMonth = 0;
    let minDay = 1;

    if (checkObjectKeyExists(minCalculatedFromToday, "year")) {
      minYear = Number(currentDate.getFullYear() - minCalculatedFromToday.year);
    }
    if (checkObjectKeyExists(minCalculatedFromToday, "month")) {
      minMonth = Number(currentDate.getMonth() - minCalculatedFromToday.month);
    }
    if (checkObjectKeyExists(minCalculatedFromToday, "day")) {
      minDay = Number(currentDate.getDate() - minCalculatedFromToday.day);
    }

    const targetDate = new Date(minYear, minMonth, minDay);

    const minDate = new TuiDay(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );

    return minDate;
  }

  /**
   * Calculates the TuiDay object for the max date based on the maxCalculatedFromToday input
   * @returns TuiDay
   */
  calculateMaxDateFromToday(maxCalculatedFromToday: {
    year?: number;
    month?: number;
    day?: number;
  }): TuiDay {
    const currentDate = new Date();

    let maxYear = 9999;
    let maxMonth = 11;
    let maxDay = 31;

    if (checkObjectKeyExists(maxCalculatedFromToday, "year")) {
      maxYear = Number(currentDate.getFullYear() + maxCalculatedFromToday.year);
    }
    if (checkObjectKeyExists(maxCalculatedFromToday, "month")) {
      maxMonth = Number(currentDate.getMonth() + maxCalculatedFromToday.month);
    }
    if (checkObjectKeyExists(maxCalculatedFromToday, "day")) {
      maxDay = Number(currentDate.getDate() + maxCalculatedFromToday.day);
    }
    const targetDate = new Date(maxYear, maxMonth, maxDay);

    const maxDate = new TuiDay(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate()
    );

    return maxDate;
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
